import axios from 'axios';
import config from '../config/config';
import { clearTenantAuthStorage } from '../utils/platformLoginGate';

const platformTokenKey = 'platform-token';
const platformUserKey = 'platform-user';

const platformApi = axios.create({
  baseURL: `${config.backendUrl}/api/platform`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

const clearPlatformAuthStorage = () => {
  // Platform oturumu yalnızca sessionStorage; eski localStorage kalıntılarını da temizle
  sessionStorage.removeItem(platformTokenKey);
  sessionStorage.removeItem(platformUserKey);
  localStorage.removeItem(platformTokenKey);
  localStorage.removeItem(platformUserKey);
};

const getPlatformToken = () => sessionStorage.getItem(platformTokenKey) || null;

platformApi.interceptors.request.use(
  (requestConfig) => {
    const token = getPlatformToken();
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => Promise.reject(error)
);

platformApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      clearPlatformAuthStorage();
    }
    return Promise.reject(error);
  }
);

export const platformAuthService = {
  login: async ({ username, password }) => {
    const response = await platformApi.post('/auth/login', { username, password });
    if (response.data?.success && response.data?.token) {
      // Tenant oturumu ile platform oturumu asla aynı anda kalmasın
      clearTenantAuthStorage();
      clearPlatformAuthStorage();
      // Kalıcı depolama yok: tarayıcı kapanınca platform oturumu biter
      sessionStorage.setItem(platformTokenKey, response.data.token);
      sessionStorage.setItem(platformUserKey, JSON.stringify({
        id: response.data.user?.id || null,
        role: response.data.user?.role || null,
        username: response.data.user?.username || null
      }));
    }
    return response.data;
  },
  logout: () => {
    clearPlatformAuthStorage();
  },
  clearPlatformAuthStorage,
  getToken: getPlatformToken,
  getUser: () => {
    const raw = sessionStorage.getItem(platformUserKey);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (_error) {
      return null;
    }
  }
};

export const platformTenantService = {
  list: async () => {
    const response = await platformApi.get('/tenants');
    return response.data;
  },
  create: async (payload) => {
    const response = await platformApi.post('/tenants', payload);
    return response.data;
  },
  update: async (tenantId, payload) => {
    const response = await platformApi.patch(`/tenants/${tenantId}`, payload);
    return response.data;
  },
  updateLicense: async (tenantId, payload) => {
    const response = await platformApi.patch(`/tenants/${tenantId}/license`, payload);
    return response.data;
  },
  renewLicense: async (tenantId, payload) => {
    const response = await platformApi.post(`/tenants/${tenantId}/license/renew`, payload);
    return response.data;
  },
  getPrimaryAdmin: async (tenantId) => {
    const response = await platformApi.get(`/tenants/${tenantId}/admin`);
    return response.data;
  },
  resetAdminPassword: async (tenantId, payload) => {
    const response = await platformApi.post(`/tenants/${tenantId}/admin-password/reset`, payload);
    return response.data;
  },
  provision: async (tenantId, payload) => {
    const response = await platformApi.post(`/tenants/${tenantId}/provision`, payload);
    return response.data;
  },
  remove: async (tenantId) => {
    const response = await platformApi.delete(`/tenants/${tenantId}`);
    return response.data;
  }
};

export const platformUserService = {
  list: async () => {
    const response = await platformApi.get('/users');
    return response.data;
  },
  create: async (payload) => {
    const response = await platformApi.post('/users', payload);
    return response.data;
  },
  update: async (platformUserId, payload) => {
    const response = await platformApi.patch(`/users/${platformUserId}`, payload);
    return response.data;
  },
  resetPassword: async (platformUserId, payload) => {
    const response = await platformApi.post(`/users/${platformUserId}/reset-password`, payload);
    return response.data;
  },
  remove: async (platformUserId) => {
    const response = await platformApi.delete(`/users/${platformUserId}`);
    return response.data;
  }
};

export const platformAnnouncementService = {
  list: async (limit = 20) => {
    const response = await platformApi.get('/announcements', { params: { limit } });
    return response.data;
  },
  listDeliveries: async (announcementId, options = {}) => {
    const { status = 'all', limit = 50, offset = 0 } = options;
    const params = {};
    if (status && status !== 'all') {
      params.status = status;
    }
    if (limit != null) {
      params.limit = limit;
    }
    if (offset != null) {
      params.offset = offset;
    }
    const response = await platformApi.get(`/announcements/${announcementId}/deliveries`, { params });
    return response.data;
  },
  retryDelivery: async (announcementId, deliveryId) => {
    const response = await platformApi.post(`/announcements/${announcementId}/deliveries/${deliveryId}/retry`);
    return response.data;
  },
  create: async (payload) => {
    const response = await platformApi.post('/announcements', payload);
    return response.data;
  }
};

export default platformApi;
